import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Copy } from 'lucide-react';
import toast from 'react-hot-toast';

import { publicProductsApi } from '../../api/publicProducts.js';
import { PageHeader } from '../../components/ui/PageHeader.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { Input } from '../../components/ui/Input.jsx';
import { Select } from '../../components/ui/Select.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Badge } from '../../components/ui/Badge.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { PlateChart } from '../../components/PlateChart.jsx';
import { fmt } from '../../lib/formatters.js';

const CRITERIA = [
  { value: 'similar', label: 'Simili' },
  { value: 'cheaper', label: 'Più economici' },
  { value: 'higher_protein', label: 'Più proteici' },
  { value: 'lower_fat', label: 'Meno grassi' },
  { value: 'same_lifestage', label: 'Stessa fase di vita' }
];

/**
 * Copy a product SKU to the clipboard with a short confirmation toast. Falls
 * back gracefully when the Clipboard API is unavailable (non-secure context).
 */
function CopySkuButton({ sku }) {
  if (!sku) return null;
  const onCopy = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(sku);
      } else {
        throw new Error('clipboard unavailable');
      }
      toast.success(`SKU ${sku} copiato`);
    } catch {
      toast.error('Impossibile copiare lo SKU');
    }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      title={`Copia SKU ${sku}`}
      aria-label={`Copia SKU ${sku}`}
      className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs text-ink-muted transition-colors hover:bg-surface-subtle hover:text-ink"
    >
      <Copy className="h-3.5 w-3.5" strokeWidth={1.75} />
      <span className="tabular-nums">{sku}</span>
    </button>
  );
}

const MACRO_ROWS = [
  { key: 'proteinPct', label: 'Proteine %' },
  { key: 'fatPct', label: 'Grassi %' },
  { key: 'fiberPct', label: 'Fibre %' },
  { key: 'ashPct', label: 'Ceneri %' },
  { key: 'moisturePct', label: 'Umidità %' },
  { key: 'kcalPerKg', label: 'kcal/kg' },
  { key: 'lifestage', label: 'Fase di vita' }
];

export function NutritionPage() {
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState([]); // [{id,name}]
  const [detailId, setDetailId] = useState(null);
  const [criterion, setCriterion] = useState('similar');
  const [inStock, setInStock] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);

  // The product text index does not cover the SKU, so a code-like term (no
  // spaces, contains a digit) is sent through the dedicated `sku` filter
  // instead of the free-text `q`. Plain words keep using `q` (text search).
  const term = q.trim();
  const looksLikeCode = term.length > 0 && !/\s/.test(term) && /\d/.test(term);
  const searchParams = looksLikeCode
    ? { sku: term, limit: 15, status: 'active' }
    : { q: term, limit: 15, status: 'active' };

  const searchQuery = useQuery({
    queryKey: ['emp-nutrition', 'search', term],
    queryFn: () => publicProductsApi.list(searchParams),
    enabled: term.length >= 2
  });

  const plateQuery = useQuery({
    queryKey: ['emp-nutrition', 'plate', detailId],
    queryFn: () => publicProductsApi.plate(detailId),
    enabled: Boolean(detailId)
  });

  const altQuery = useQuery({
    queryKey: ['emp-nutrition', 'alt', detailId, criterion, inStock],
    queryFn: () => publicProductsApi.alternatives(detailId, { criterion, inStock, limit: 6 }),
    enabled: Boolean(detailId)
  });

  const compareQuery = useQuery({
    queryKey: ['emp-nutrition', 'compare', selected.map((s) => s.id).join(',')],
    queryFn: () => publicProductsApi.compare(selected.map((s) => s.id)),
    enabled: compareOpen && selected.length >= 2
  });

  const isSelected = (id) => selected.some((s) => s.id === id);
  const toggleSelect = (p) => {
    setSelected((prev) => {
      if (prev.some((s) => s.id === p._id)) return prev.filter((s) => s.id !== p._id);
      if (prev.length >= 6) return prev;
      return [...prev, { id: p._id, name: p.name }];
    });
  };

  const items = (searchQuery.data && searchQuery.data.items) || [];
  const detailName = (selected.find((s) => s.id === detailId) || {}).name
    || (items.find((p) => p._id === detailId) || {}).name
    || (altQuery.data && altQuery.data.reference && altQuery.data.reference.name)
    || '';

  return (
    <div className="space-y-6">
      <PageHeader title="Nutrizione" description="Confronta formulazioni, visualizza il Piatto ORME e trova alternative." />

      <Card>
        <Input
          label="Cerca prodotto"
          placeholder="Nome o SKU (min. 2 caratteri)"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        {q.trim().length >= 2 && (
          <div className="mt-3 divide-y divide-border">
            {searchQuery.isLoading && <p className="py-3 text-sm text-ink-muted">Ricerca…</p>}
            {searchQuery.isSuccess && items.length === 0 && <p className="py-3 text-sm text-ink-muted">Nessun prodotto.</p>}
            {items.map((p) => (
              <div key={p._id} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-ink">{p.name}</p>
                  <p className="truncate text-xs text-ink-muted">{p.sku}{p.brand ? ` · ${p.brand}` : ''}{p.price ? ` · ${fmt.eur(p.price.amount)}` : ''}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <CopySkuButton sku={p.sku} />
                  <Button variant={isSelected(p._id) ? 'primary' : 'secondary'} onClick={() => toggleSelect(p)}>
                    {isSelected(p._id) ? 'Rimuovi' : 'Confronta'}
                  </Button>
                  <Button variant="ghost" onClick={() => setDetailId(p._id)}>Piatto</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {selected.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-ink-muted">Da confrontare:</span>
          {selected.map((s) => (
            <Badge key={s.id} tone="neutral">{s.name}</Badge>
          ))}
          <Button disabled={selected.length < 2} onClick={() => setCompareOpen(true)}>Confronta ({selected.length})</Button>
          <Button variant="ghost" onClick={() => setSelected([])}>Svuota</Button>
        </div>
      )}

      {detailId && (
        <Card>
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-base font-semibold text-ink">Piatto ORME · {detailName}</h3>
            <Button variant="ghost" onClick={() => setDetailId(null)}>Chiudi</Button>
          </div>
          {plateQuery.isLoading && <p className="text-sm text-ink-muted">Carico…</p>}
          {plateQuery.isSuccess && (
            <div style={{ '--plate-hole': 'var(--color-surface-elevated)' }}>
              <PlateChart plate={plateQuery.data} />
            </div>
          )}

          <div className="mt-6 border-t border-border pt-4">
            <div className="mb-3 flex flex-wrap items-end gap-3">
              <h4 className="text-sm font-semibold text-ink">Alternative</h4>
              <Select label="Criterio" value={criterion} onChange={(e) => setCriterion(e.target.value)}>
                {CRITERIA.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </Select>
              <label className="flex items-center gap-2 pb-2 text-sm text-ink">
                <input type="checkbox" checked={inStock} onChange={(e) => setInStock(e.target.checked)} /> Solo disponibili qui
              </label>
            </div>
            {altQuery.isLoading && <p className="text-sm text-ink-muted">Carico…</p>}
            {altQuery.isSuccess && (altQuery.data.alternatives || []).length === 0 && (
              <p className="text-sm text-ink-muted">Nessuna alternativa trovata con questo criterio.</p>
            )}
            <div className="divide-y divide-border">
              {altQuery.isSuccess && (altQuery.data.alternatives || []).map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-3 py-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-ink">{a.name}</p>
                    <p className="truncate text-xs text-ink-muted">
                      {a.brand ? `${a.brand} · ` : ''}{a.proteinPct != null ? `Prot ${a.proteinPct}% · ` : ''}{a.fatPct != null ? `Grassi ${a.fatPct}% · ` : ''}{a.price ? fmt.eur(a.price.amount) : ''}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <CopySkuButton sku={a.sku} />
                    {a.inStockHere && <Badge tone="success">In stock</Badge>}
                    <Button variant={isSelected(a.id) ? 'primary' : 'secondary'} onClick={() => toggleSelect({ _id: a.id, name: a.name })}>
                      {isSelected(a.id) ? 'Rimuovi' : 'Confronta'}
                    </Button>
                    <Button variant="ghost" onClick={() => setDetailId(a.id)}>Piatto</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      <Modal open={compareOpen} onClose={() => setCompareOpen(false)} title="Confronto formulazioni" size="xl">
        {compareQuery.isLoading && <p className="text-sm text-ink-muted">Carico…</p>}
        {compareQuery.isSuccess && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-ink-muted">Valore</th>
                  {compareQuery.data.products.map((p) => (
                    <th key={p.id} className="px-3 py-2 text-left font-medium text-ink">{p.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {MACRO_ROWS.map((row) => (
                  <tr key={row.key}>
                    <td className="px-3 py-2 text-ink-muted">{row.label}</td>
                    {compareQuery.data.products.map((p) => (
                      <td key={p.id} className="px-3 py-2 tabular-nums text-ink">{(p.nutritionalProfile && p.nutritionalProfile[row.key] != null) ? p.nutritionalProfile[row.key] : '-'}</td>
                    ))}
                  </tr>
                ))}
                <tr>
                  <td className="px-3 py-2 text-ink-muted">Prezzo</td>
                  {compareQuery.data.products.map((p) => (
                    <td key={p.id} className="px-3 py-2 text-ink">{p.price ? fmt.eur(p.price.amount) : '-'}</td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Modal>
    </div>
  );
}
